"use client";

import { logger } from "@/utils/logger";

// Database configuration
const DB_NAME = "avezor-form-storage";
const DB_VERSION = 1;
const OBJECT_STORE_NAME = "formData";

/**
 * Initialize the IndexedDB database
 */
const initDB = async (): Promise<IDBDatabase> => {
  return new Promise((resolve, reject) => {
    if (typeof window === "undefined" || !window.indexedDB) {
      reject(new Error("IndexedDB is not supported in this environment"));
      return;
    }

    const request = indexedDB.open(DB_NAME, DB_VERSION);

    request.onerror = (event) => {
      logger.error("Error opening IndexedDB", {
        error: (event.target as IDBOpenDBRequest).error?.message,
      });
      reject((event.target as IDBOpenDBRequest).error);
    };

    request.onupgradeneeded = (event) => {
      const db = (event.target as IDBOpenDBRequest).result;

      // Create the object store if it doesn't exist
      if (!db.objectStoreNames.contains(OBJECT_STORE_NAME)) {
        db.createObjectStore(OBJECT_STORE_NAME);
      }
    };

    request.onsuccess = (event) => {
      const db = (event.target as IDBOpenDBRequest).result;
      resolve(db);
    };
  });
};

/**
 * Get an object from IndexedDB
 * @param key The key to retrieve
 * @returns The object or null if not found
 */
export const getIndexedObject = async <T>(key: string): Promise<T | null> => {
  try {
    if (typeof window === "undefined" || !window.indexedDB) {
      return null;
    }

    const db = await initDB();

    return new Promise((resolve, reject) => {
      const transaction = db.transaction(OBJECT_STORE_NAME, "readonly");
      const store = transaction.objectStore(OBJECT_STORE_NAME);
      const request = store.get(key);

      request.onerror = (event) => {
        logger.error(`Error getting ${key} from IndexedDB:`, {
          error: (event.target as IDBRequest).error?.message,
        });
        reject((event.target as IDBRequest).error);
      };

      request.onsuccess = (event) => {
        const result = (event.target as IDBRequest).result;
        resolve(result || null);
      };

      transaction.oncomplete = () => {
        db.close();
      };
    });
  } catch (error) {
    logger.error(`Error getting ${key} from IndexedDB:`, {
      error: error instanceof Error ? error.message : String(error),
    });
    return null;
  }
};

/**
 * Set an object in IndexedDB
 * @param key The key to store
 * @param value The value to store
 */
export const setIndexedObject = async <T>(
  key: string,
  value: T,
): Promise<void> => {
  try {
    if (typeof window === "undefined" || !window.indexedDB) {
      return;
    }

    const db = await initDB();

    return new Promise((resolve, reject) => {
      const transaction = db.transaction(OBJECT_STORE_NAME, "readwrite");
      const store = transaction.objectStore(OBJECT_STORE_NAME);
      const request = store.put(value, key);

      request.onerror = (event) => {
        logger.error(`Error setting ${key} in IndexedDB:`, {
          error: (event.target as IDBRequest).error?.message,
        });
        reject((event.target as IDBRequest).error);
      };

      request.onsuccess = () => {
        resolve();
      };

      transaction.oncomplete = () => {
        db.close();
      };
    });
  } catch (error) {
    logger.error(`Error setting ${key} in IndexedDB:`, {
      error: error instanceof Error ? error.message : String(error),
    });
  }
};

/**
 * Remove an object from IndexedDB
 * @param key The key to remove
 */
export const removeIndexedObject = async (key: string): Promise<void> => {
  try {
    if (typeof window === "undefined" || !window.indexedDB) {
      return;
    }

    const db = await initDB();

    return new Promise((resolve, reject) => {
      const transaction = db.transaction(OBJECT_STORE_NAME, "readwrite");
      const store = transaction.objectStore(OBJECT_STORE_NAME);
      const request = store.delete(key);

      request.onerror = (event) => {
        logger.error(`Error removing ${key} from IndexedDB:`, {
          error: (event.target as IDBRequest).error?.message,
        });
        reject((event.target as IDBRequest).error);
      };

      request.onsuccess = () => {
        resolve();
      };

      transaction.oncomplete = () => {
        db.close();
      };
    });
  } catch (error) {
    logger.error(`Error removing ${key} from IndexedDB:`, {
      error: error instanceof Error ? error.message : String(error),
    });
  }
};

/**
 * Get all keys in the IndexedDB store that start with a prefix
 * @param prefix The prefix to match
 * @returns Array of keys
 */
export const getIndexedKeysWithPrefix = async (
  prefix: string,
): Promise<string[]> => {
  try {
    if (typeof window === "undefined" || !window.indexedDB) {
      return [];
    }

    const db = await initDB();

    return new Promise((resolve, reject) => {
      const keys: string[] = [];
      const transaction = db.transaction(OBJECT_STORE_NAME, "readonly");
      const store = transaction.objectStore(OBJECT_STORE_NAME);
      const request = store.getAllKeys();

      request.onerror = (event) => {
        logger.error(
          `Error getting keys with prefix ${prefix} from IndexedDB:`,
          {
            error: (event.target as IDBRequest).error?.message,
          },
        );
        reject((event.target as IDBRequest).error);
      };

      request.onsuccess = (event) => {
        const allKeys = (event.target as IDBRequest).result as IDBValidKey[];

        // Filter keys that start with the prefix
        allKeys.forEach((key) => {
          if (typeof key === "string" && key.startsWith(prefix)) {
            keys.push(key);
          }
        });

        resolve(keys);
      };

      transaction.oncomplete = () => {
        db.close();
      };
    });
  } catch (error) {
    logger.error(`Error getting keys with prefix ${prefix} from IndexedDB:`, {
      error: error instanceof Error ? error.message : String(error),
    });
    return [];
  }
};

/**
 * Clear all entries in the IndexedDB store that match a prefix
 * @param prefix The prefix to match
 */
export const clearIndexedObjectsByPrefix = async (
  prefix: string,
): Promise<void> => {
  try {
    if (typeof window === "undefined" || !window.indexedDB) {
      return;
    }

    const keys = await getIndexedKeysWithPrefix(prefix);
    if (keys.length === 0) {
      return;
    }

    const db = await initDB();
    const transaction = db.transaction(OBJECT_STORE_NAME, "readwrite");
    const store = transaction.objectStore(OBJECT_STORE_NAME);

    // Delete each key that matches the prefix
    await Promise.all(
      keys.map((key) => {
        return new Promise<void>((resolve, reject) => {
          const request = store.delete(key);

          request.onerror = (event) => {
            logger.error(`Error removing ${key} from IndexedDB:`, {
              error: (event.target as IDBRequest).error?.message,
            });
            reject((event.target as IDBRequest).error);
          };

          request.onsuccess = () => {
            resolve();
          };
        });
      }),
    );

    db.close();
    logger.debug(
      `Cleared ${keys.length} entries with prefix ${prefix} from IndexedDB`,
    );
  } catch (error) {
    logger.error(
      `Error clearing entries with prefix ${prefix} from IndexedDB:`,
      {
        error: error instanceof Error ? error.message : String(error),
      },
    );
  }
};

/**
 * Completely delete the entire IndexedDB database ("avezor-form-storage")
 */
export const clearEntireIndexedDB = async (): Promise<void> => {
  return new Promise((resolve, reject) => {
    if (typeof window === "undefined" || !window.indexedDB) {
      resolve();
      return;
    }

    const request = indexedDB.deleteDatabase(DB_NAME);

    request.onerror = (event) => {
      logger.error(`Error deleting IndexedDB database ${DB_NAME}:`, {
        error: (event.target as IDBRequest).error?.message,
      });
      reject((event.target as IDBRequest).error);
    };

    request.onsuccess = () => {
      logger.debug(`IndexedDB database ${DB_NAME} successfully deleted`);
      resolve();
    };

    request.onblocked = () => {
      logger.warn(
        `Deletion of IndexedDB database ${DB_NAME} is blocked. Close all tabs.`,
      );
    };
  });
};
